import { ActionIcon, Popover, Tooltip, Badge, Collapse } from "@mantine/core";
import {
    IconBell,
    IconCheck,
    IconChevronDown,
    IconChevronRight,
    IconChevronUp,
    IconReceiptOff,
    IconCash,
    IconCalendarX,
    IconUsers,
    IconUserCheck,
    IconTicket,
    IconMessages,
    IconClock,
    IconPlaneDeparture,
    IconInfoCircle,
    IconChecklist
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useNotifications, useMarkAllNotificationsSeen, useSaveNotification } from "@/hooks/api-hooks";
import { navigate } from "@/lib/navigation";
import { useState, useMemo } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { formatDayLong, formatPrice } from "@/lib/format-utils";

dayjs.extend(relativeTime);

type NotificationCategoryKey = 'payments' | 'reservations' | 'tickets' | 'flights' | 'system';

interface CategoryConfig {
    key: NotificationCategoryKey;
    icon: React.ElementType;
}

const CATEGORY_CONFIGS: Record<NotificationCategoryKey, CategoryConfig> = {
    payments: {
        key: 'payments',
        icon: IconCash
    },
    reservations: {
        key: 'reservations',
        icon: IconCalendarX
    },
    tickets: {
        key: 'tickets',
        icon: IconTicket
    },
    flights: {
        key: 'flights',
        icon: IconPlaneDeparture
    },
    system: {
        key: 'system',
        icon: IconBell
    }
};

const getNotificationCategory = (type: string): NotificationCategoryKey => {
    switch (type) {
        case 'reservation_payment_overdue':
        case 'payment_overdue':
        case 'payment_request':
            return 'payments';
        case 'reservation_deleted':
        case 'booking_pax_count_mismatch':
            return 'reservations';
        case 'ticket_assigned':
        case 'ticket_updated':
        case 'ticket_commented':
        case 'ticket_overdue':
            return 'tickets';
        case 'flight_status_changed':
            return 'flights';
        default:
            return 'system';
    }
};

const getNotificationIconMeta = (type: string) => {
    switch (type) {
        case 'reservation_payment_overdue':
        case 'payment_overdue':
            return { icon: IconReceiptOff };
        case 'payment_request':
            return { icon: IconCash };
        case 'reservation_deleted':
            return { icon: IconCalendarX };
        case 'booking_pax_count_mismatch':
            return { icon: IconUsers };
        case 'ticket_assigned':
            return { icon: IconUserCheck };
        case 'ticket_updated':
            return { icon: IconTicket };
        case 'ticket_commented':
            return { icon: IconMessages };
        case 'ticket_overdue':
            return { icon: IconClock };
        case 'flight_status_changed':
            return { icon: IconPlaneDeparture };
        default:
            return { icon: IconInfoCircle };
    }
};

export default function NotificationsDropdown() {
    const { t } = useTranslation();
    const [opened, setOpened] = useState(false);

    const { data: response } = useNotifications();

    const notifications = response?.data || [];
    const unreadCount = response?.unread_count || 0;

    const markAllSeenValues = useMarkAllNotificationsSeen();
    const saveNotification = useSaveNotification();

    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
        payments: true,
        reservations: true,
        tickets: true,
        flights: true,
        system: true
    });

    const handleMarkAllSeen = (e: React.MouseEvent) => {
        e.stopPropagation();
        markAllSeenValues.mutate();
    };

    const handleMarkSectionSeen = (e: React.MouseEvent, sectionItems: any[]) => {
        e.stopPropagation();
        const unreadSectionItems = sectionItems.filter((item) => !item.seen);
        unreadSectionItems.forEach((item) => {
            saveNotification.mutate({ id: item.id, seen: true });
        });
    };

    const handleMarkOneSeen = (e: React.MouseEvent, id: string | number) => {
        e.stopPropagation();
        saveNotification.mutate({ id, seen: true });
    };

    const toggleSection = (categoryKey: string) => {
        setExpandedSections((prev) => ({
            ...prev,
            [categoryKey]: !prev[categoryKey]
        }));
    };

    const toggleAllSections = () => {
        const allExpanded = activeCategories.every((catKey) => expandedSections[catKey] ?? true);
        setExpandedSections((prev) => {
            const updated = { ...prev };
            activeCategories.forEach((catKey) => {
                updated[catKey] = !allExpanded;
            });
            return updated;
        });
    };

    const resolveNotificationText = (n: any) => {
        if (!n) return { title: '', content: '' };

        let title = n.title;
        let content = n.content;

        if (n.type === 'reservation_payment_overdue' && n.data) {
            title = t('notifications.reservation_payment_overdue.title');
            content = t('notifications.reservation_payment_overdue.content', {
                reservation_id: n.data.reservation_id,
                amount: formatPrice(n.data.expected_amount),
                currency: n.data.currency,
                due_date: formatDayLong(n.data.due_date)
            });
        }

        if (n.type === 'payment_overdue' && n.data) {
            title = t('notifications.payment_overdue.title');
            content = t('notifications.payment_overdue.content', {
                payment_id: n.data.payment_id,
                amount: formatPrice(n.data.amount),
                currency: n.data.currency,
                due_date: formatDayLong(n.data.due_date)
            });
        }

        if (n.type === 'reservation_deleted' && n.data) {
            title = t('notifications.reservation_deleted.title');
            content = t('notifications.reservation_deleted.content', {
                reservation_id: n.data.reservation_id,
                deleter_name: n.data.deleter_name
            });
        }

        if (n.type === 'payment_request' && n.data) {
            title = t('notifications.payment_request.title');
            content = t('notifications.payment_request.content', {
                from_business_name: n.data.from_business_name,
                amount: n.data.amount,
                currency: n.data.currency
            });
        }

        if (n.type === 'ticket_updated' && n.data) {
            title = t('notifications.ticket_updated.title');
            content = t('notifications.ticket_updated.content', {
                updater_name: n.data.updater_name,
                ticket_title: n.data.ticket_title
            });
        }

        if (n.type === 'ticket_assigned' && n.data) {
            title = t('notifications.ticket_assigned.title');
            content = t('notifications.ticket_assigned.content', {
                ticket_title: n.data.ticket_title
            });
        }

        if (n.type === 'ticket_commented' && n.data) {
            title = t('notifications.ticket_commented.title');
            content = t('notifications.ticket_commented.content', {
                commenter_name: n.data.commenter_name,
                ticket_title: n.data.ticket_title
            });
        }

        if (n.type === 'ticket_overdue' && n.data) {
            title = t('notifications.ticket_overdue.title');
            content = t('notifications.ticket_overdue.content', {
                ticket_title: n.data.ticket_title
            });
        }

        if (n.type === 'booking_pax_count_mismatch' && n.data) {
            title = t('notifications.booking_pax_count_mismatch.title');
            content = t('notifications.booking_pax_count_mismatch.content', {
                reservation_id: n.data.reservation_id
            });
        }

        if (n.type === 'flight_status_changed' && n.data) {
            title = t('notifications.flight_status_changed.title');
            content = t('notifications.flight_status_changed.content', {
                flight_number: n.data.flight_number,
                new_status: n.data.new_status
            });
        }

        if (title && title.includes('.')) title = t(title);
        if (content && content.includes('.') && !content.includes(' ')) content = t(content);

        return { title, content };
    };

    const groupedNotifications = useMemo(() => {
        const groups: Record<NotificationCategoryKey, any[]> = {
            payments: [],
            reservations: [],
            tickets: [],
            flights: [],
            system: []
        };

        notifications.forEach((item: any) => {
            const cat = getNotificationCategory(item.type);
            groups[cat].push(item);
        });

        return groups;
    }, [notifications]);

    const activeCategories = useMemo(() => {
        const keys: NotificationCategoryKey[] = ['payments', 'reservations', 'tickets', 'flights', 'system'];
        return keys.filter((key) => groupedNotifications[key] && groupedNotifications[key].length > 0);
    }, [groupedNotifications]);

    const areAllSectionsExpanded = useMemo(() => {
        return activeCategories.length > 0 && activeCategories.every((catKey) => expandedSections[catKey] ?? true);
    }, [activeCategories, expandedSections]);

    return (
        <Popover
            opened={opened}
            onChange={setOpened}
            position="bottom-end"
            shadow="xl"
            radius="lg"
            width={540}
            withinPortal
        >
            <Popover.Target>
                <ActionIcon
                    onClick={() => setOpened((o) => !o)}
                    size={38}
                    radius="xl"
                    variant="subtle"
                    color="gray"
                    className="relative transition-transform active:scale-95"
                >
                    <IconBell size={20} stroke={1.5} className="text-gray-700" />
                    {unreadCount > 0 && (
                        <div className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 bg-red-500 text-white font-bold text-[8px] rounded-full flex items-center justify-center pointer-events-none ring-2 ring-white">
                            {unreadCount > 99 ? '99+' : unreadCount}
                        </div>
                    )}
                </ActionIcon>
            </Popover.Target>

            <Popover.Dropdown p={0} className="border border-gray-200 overflow-hidden shadow-2xl">
                {/* Header: no more */}
                {/* <div className="border-b border-gray-200 p-3.5 flex justify-between items-center bg-gray-50/90 backdrop-blur-sm">
                    <div className="flex items-center gap-2.5">
                        <div className="font-semibold text-gray-900 text-base flex items-center gap-2">
                            {t('notifications_title')}
                            {unreadCount > 0 && (
                                <Badge color="dark" size="sm" variant="filled" circle>
                                    {unreadCount}
                                </Badge>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {activeCategories.length > 1 && (
                            <Tooltip label={areAllSectionsExpanded ? t('collapse_all') : t('expand_all')}>
                                <ActionIcon
                                    size="sm"
                                    variant="light"
                                    color="gray"
                                    onClick={toggleAllSections}
                                >
                                    {areAllSectionsExpanded ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
                                </ActionIcon>
                            </Tooltip>
                        )}

                        {unreadCount > 0 && (
                            <button
                                type="button"
                                onClick={handleMarkAllSeen}
                                className="text-xs text-gray-700 hover:text-gray-900 cursor-pointer font-medium flex items-center gap-1 hover:underline transition-colors"
                            >
                                <IconCheck size={14} /> {t('mark_all_as_read')}
                            </button>
                        )}
                    </div>
                </div> */}

                {/* Main scrollable body */}
                <div className="max-h-[520px] overflow-y-auto divide-y divide-gray-100">
                    {activeCategories.length === 0 ? (
                        <div className="p-10 text-center text-sm text-gray-500 flex flex-col items-center justify-center gap-3">
                            <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                                <IconBell size={28} stroke={1.5} />
                            </div>
                            <span className="font-medium text-gray-600">{t('no_notifications')}</span>
                        </div>
                    ) : (
                        activeCategories.map((catKey) => {
                            const config = CATEGORY_CONFIGS[catKey];
                            const sectionItems = groupedNotifications[catKey];
                            const sectionUnreadCount = sectionItems.filter((i) => !i.seen).length;
                            const isExpanded = expandedSections[catKey] ?? true;
                            const CategoryHeaderIcon = config.icon;

                            return (
                                <div key={catKey} className="bg-white">
                                    {/* Section Header */}
                                    <div
                                        onClick={() => toggleSection(catKey)}
                                        className="px-3.5 py-2.5 flex items-center justify-between cursor-pointer select-none border-b border-gray-100 bg-gray-50/80 hover:bg-gray-100/80 transition-colors"
                                    >
                                        <div className="flex items-center gap-2.5">
                                            <div className="text-gray-500">
                                                {isExpanded ? (
                                                    <IconChevronDown size={16} />
                                                ) : (
                                                    <IconChevronRight size={16} />
                                                )}
                                            </div>
                                            <CategoryHeaderIcon size={18} className="text-gray-700" />
                                            <span className="text-xs font-bold uppercase tracking-wider text-gray-700">
                                                {t(`notifications_categories.${catKey}`)}
                                            </span>
                                            {sectionUnreadCount > 0 && (
                                                <Badge
                                                    size="xs"
                                                    variant="filled"
                                                    color="dark"
                                                    circle
                                                >
                                                    {sectionUnreadCount}
                                                </Badge>
                                            )}
                                        </div>

                                        {sectionUnreadCount > 0 && (
                                            <Tooltip label={t('mark_section_as_read')}>
                                                <button
                                                    type="button"
                                                    onClick={(e) => handleMarkSectionSeen(e, sectionItems)}
                                                    className="p-1 rounded text-xs font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-200/60 flex items-center gap-1 transition-colors"
                                                >
                                                    <IconChecklist size={14} />
                                                    <span className="hidden sm:inline">{t('mark_section_as_read')}</span>
                                                </button>
                                            </Tooltip>
                                        )}
                                    </div>

                                    {/* Section Content */}
                                    <Collapse expanded={isExpanded}>
                                        <div className="divide-y divide-gray-100">
                                            {sectionItems.map((item: any) => {
                                                const { title, content } = resolveNotificationText(item);
                                                const iconMeta = getNotificationIconMeta(item.type);
                                                const ItemIcon = iconMeta.icon;

                                                return (
                                                    <div
                                                        key={item.id}
                                                        onClick={() => {
                                                            if (item.data?.link) {
                                                                navigate(item.data.link);
                                                            } else if (item.href) {
                                                                navigate(item.href);
                                                            }
                                                            setOpened(false);
                                                        }}
                                                        className={`relative group p-3.5 hover:bg-gray-100/60 cursor-pointer flex gap-3.5 items-start transition-colors ${!item.seen ? 'bg-gray-50/80 font-normal' : ''
                                                            }`}
                                                    >
                                                        {/* Type Icon */}
                                                        <div
                                                            className="w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center border bg-gray-100 text-gray-700 border-gray-200"
                                                        >
                                                            <ItemIcon size={18} />
                                                        </div>

                                                        {/* Text Content */}
                                                        <div className="flex-1 min-w-0 pr-8">
                                                            <div className="flex items-center gap-2">
                                                                <div
                                                                    className={`text-sm text-gray-900 line-clamp-1 ${!item.seen ? 'font-semibold' : 'font-medium'
                                                                        }`}
                                                                >
                                                                    {title}
                                                                </div>
                                                                {!item.seen && (
                                                                    <div className="w-2 h-2 rounded-full bg-gray-900 shrink-0" />
                                                                )}
                                                            </div>
                                                            <div className="text-xs text-gray-600 mt-0.5 line-clamp-2 leading-relaxed">
                                                                {content || item.subtitle}
                                                            </div>
                                                            <div className="text-[10px] text-gray-400 mt-1.5 font-medium">
                                                                {dayjs(item.created_at).fromNow()}
                                                            </div>
                                                        </div>

                                                        {/* Individual Mark Read Button */}
                                                        {!item.seen && (
                                                            <Tooltip label={t('mark_as_read')}>
                                                                <ActionIcon
                                                                    size="sm"
                                                                    radius="md"
                                                                    variant="light"
                                                                    color="gray"
                                                                    onClick={(e) => handleMarkOneSeen(e, item.id)}
                                                                    className="absolute right-3.5 top-3.5 opacity-0 group-hover:opacity-100 transition-opacity"
                                                                >
                                                                    <IconCheck size={14} />
                                                                </ActionIcon>
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </Collapse>
                                </div>
                            );
                        })
                    )}
                </div>
            </Popover.Dropdown>
        </Popover>
    );
}
